import type { CsvPreviewRequest } from "@moneymap/contracts";

const MAX_FILE_BYTES = 20_000_000;
const MAX_ROWS = 50_000;
const delimiters = [",", ";", "\t", "|"] as const;

export type PreparedCsv = Omit<CsvPreviewRequest, "accountId"> & {
  headers: string[];
  localWarnings: string[];
};

function detectEncoding(bytes: Uint8Array): CsvPreviewRequest["encoding"] {
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) return "utf-8-bom";
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return "utf-16le";
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return "utf-16be";
  return "utf-8";
}

function decode(bytes: Uint8Array, encoding: CsvPreviewRequest["encoding"]): string {
  const decoderName = encoding === "utf-8-bom" ? "utf-8" : encoding;
  const decoded = new TextDecoder(decoderName, { fatal: true }).decode(bytes);
  return decoded.charCodeAt(0) === 0xfeff ? decoded.slice(1) : decoded;
}

function countOutsideQuotes(line: string, delimiter: string): number {
  let count = 0;
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"') {
      if (quoted && line[index + 1] === '"') {
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === delimiter && !quoted) {
      count += 1;
    }
  }
  return count;
}

function detectDelimiter(text: string): CsvPreviewRequest["delimiter"] {
  const sampleLines = text.split(/\r?\n/).filter(Boolean).slice(0, 8);
  let best: CsvPreviewRequest["delimiter"] = ",";
  let bestScore = -1;
  for (const delimiter of delimiters) {
    const counts = sampleLines.map((line) => countOutsideQuotes(line, delimiter));
    const nonzero = counts.filter((count) => count > 0);
    if (nonzero.length === 0) continue;
    const first = nonzero[0] ?? 0;
    const consistent = nonzero.filter((count) => count === first).length;
    const score = consistent * 100 + first;
    if (score > bestScore) {
      best = delimiter;
      bestScore = score;
    }
  }
  return best;
}

function parseMatrix(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (character === '"') {
      if (quoted && text[index + 1] === '"') {
        field += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }
    if (character === delimiter && !quoted) {
      row.push(field);
      field = "";
      continue;
    }
    if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && text[index + 1] === "\n") index += 1;
      row.push(field);
      if (row.some((value) => value.trim() !== "")) rows.push(row);
      row = [];
      field = "";
      if (rows.length > MAX_ROWS + 1)
        throw new Error("This CSV has more than 50,000 rows. Split it into smaller files.");
      continue;
    }
    field += character;
  }

  if (quoted)
    throw new Error(
      "The CSV ends inside a quoted field. Check the final rows and export it again.",
    );
  row.push(field);
  if (row.some((value) => value.trim() !== "")) rows.push(row);
  return rows;
}

function uniqueHeaders(values: string[]): string[] {
  const seen = new Map<string, number>();
  return values.map((value, index) => {
    const base = value.trim() || "Column " + String(index + 1);
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count === 0 ? base : base + " (" + String(count + 1) + ")";
  });
}

async function sha256(bytes: Uint8Array): Promise<string> {
  const buffer = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(buffer))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

async function readFileBytes(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === "function") {
    return new Uint8Array(await file.arrayBuffer());
  }

  // Older WebKit releases and DOM test implementations expose FileReader
  // without Blob.arrayBuffer(). Keeping the fallback here also makes CSV
  // ingestion tolerant of those supported browser environments.
  return await new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("IterNest Money could not read this CSV file."));
    reader.onload = () => {
      if (!(reader.result instanceof ArrayBuffer)) {
        reject(new Error("IterNest Money could not read this CSV file as binary data."));
        return;
      }
      resolve(new Uint8Array(reader.result));
    };
    reader.readAsArrayBuffer(file);
  });
}

export async function prepareCsvFile(file: File): Promise<PreparedCsv> {
  if (file.size === 0) throw new Error("Choose a CSV file that contains transaction rows.");
  if (file.size > MAX_FILE_BYTES)
    throw new Error("This file is larger than 20 MB. Split it into smaller files.");
  if (!file.name.toLowerCase().endsWith(".csv")) {
    throw new Error("Choose a .csv file exported by your bank or card provider.");
  }

  const bytes = await readFileBytes(file);
  const encoding = detectEncoding(bytes);
  let text: string;
  try {
    text = decode(bytes, encoding);
  } catch {
    throw new Error(
      "IterNest Money could not decode this file safely. Export it as UTF-8 or UTF-16 CSV.",
    );
  }
  const delimiter = detectDelimiter(text);
  const matrix = parseMatrix(text, delimiter);
  if (matrix.length < 2)
    throw new Error("The CSV needs a header row and at least one transaction row.");
  const headers = uniqueHeaders(matrix[0] ?? []);
  if (headers.length < 2)
    throw new Error("IterNest Money could not detect separate columns. Check the CSV delimiter.");

  const localWarnings: string[] = [];
  const rows = matrix.slice(1).map((values, index) => {
    if (values.length !== headers.length) {
      localWarnings.push(
        "Row " +
          String(index + 2) +
          " has " +
          String(values.length) +
          " columns; the header has " +
          String(headers.length) +
          ".",
      );
    }
    const record: Record<string, string> = {};
    for (let column = 0; column < headers.length; column += 1) {
      const header = headers[column];
      if (header) record[header] = values[column]?.trim() ?? "";
    }
    return record;
  });

  return {
    fileName: file.name,
    fileSize: file.size,
    fileHash: await sha256(bytes),
    encoding,
    delimiter,
    dateFormat: "auto",
    decimalSeparator: ".",
    rows,
    headers,
    localWarnings: localWarnings.slice(0, 20),
  };
}
